import _ from 'lodash';
import { getState } from '~src/state';
import { getSelectedFormatId } from '~loader/loader-utils';
import getGraphQlClient from '~graphql/getGraphQlClient';
import { onErrorShowMessage } from '~src/errors';
import {
  GET_TEMPLATES_DEFAULT,
  GET_TEMPLATE_DEFAULT_BY_ID,
  GET_TEMPLATE_BY_ID,
} from '~graphql/queries/editorTemplates';
import { unmarshalGraphqlResponse } from '~graphql/utils';
import { isLoggedUserGlobalEditor } from '~src/user';
import { FEATURES, isFeatureEnabled } from '../../features';

const limit = 100;

const TEMPLATE_CONNECTIONS = [
  {
    responseKey: 'templateCustomTypeDefaultFormatDefault',
    afterVariable: 'customTypeDefaultFormatDefaultAfter',
    skipVariable: 'skipCustomTypeDefaultFormatDefault',
  },
  {
    responseKey: 'templateCustomTypeDefaultFormatCustom',
    afterVariable: 'customTypeDefaultFormatCustomAfter',
    skipVariable: 'skipCustomTypeDefaultFormatCustom',
  },
  {
    responseKey: 'templateCustomTypeCustomFormatDefault',
    afterVariable: 'customTypeCustomFormatDefaultAfter',
    skipVariable: 'skipCustomTypeCustomFormatDefault',
  },
  {
    responseKey: 'templateCustomTypeCustomFormatCustom',
    afterVariable: 'customTypeCustomFormatCustomAfter',
    skipVariable: 'skipCustomTypeCustomFormatCustom',
  },
  {
    responseKey: 'findEditorTemplatesDefault',
    afterVariable: 'defaultTemplatesAfter',
    skipVariable: 'skipDefaultTemplates',
  },
];

const TEMPLATE_CATEGORY_CONNECTIONS = [
  'editorTemplatesDefaultCategories',
  'editorTemplatesCategoriesOfDefaultProjectType',
  'editorTemplatesCategoriesOfCustomProjectType',
];

const connectionNodes = (connection) =>
  connection?.edges?.flatMap((edge) => (edge?.node ? [edge.node] : [])) || [];

export const fetchEditorTemplateById = async (id, isGlobal) => {
  try {
    const graphQlClient = await getGraphQlClient();

    const response = await graphQlClient.query({
      fetchPolicy: 'network-only',
      variables: {
        id,
      },
      query: isGlobal ? GET_TEMPLATE_DEFAULT_BY_ID : GET_TEMPLATE_BY_ID,
    });

    return unmarshalGraphqlResponse(
      response.data,
      isGlobal ? 'getEditorTemplatesDefault' : 'getEditorTemplates'
    );
  } catch (error) {
    onErrorShowMessage();
    console.error(error);
  }
};

export const fetchEditorTemplates = async (forceFetch = false) => {
  const projectType = getState('selected.tipo');
  const formatId = getSelectedFormatId();

  const allowNew = await isFeatureEnabled(FEATURES.CREATE_TEMPLATES, false);
  const allowNewCategory = await isFeatureEnabled(
    FEATURES.CREATE_TEMPLATES_CATEGORIES
  );

  const isGlobalEditor = await isLoggedUserGlobalEditor();

  const paramsDefault = {
    defaultProjectTypeId: projectType.objectId,
    defaultFormatId: formatId,
    formatId,
    projectTypeId: projectType.objectId,
    limit,
  };

  try {
    const graphQlClient = await getGraphQlClient();
    const paginationVariables = {};

    TEMPLATE_CONNECTIONS.forEach(({ afterVariable, skipVariable }) => {
      paginationVariables[afterVariable] = null;
      paginationVariables[skipVariable] = false;
    });

    let items = [];
    let categories = [];
    let pageIndex = 0;
    let hasNextPage = true;

    while (hasNextPage) {
      const response = await graphQlClient.query({
        fetchPolicy:
          forceFetch || pageIndex > 0 ? 'network-only' : 'cache-first',
        variables: {
          ...paramsDefault,
          ...paginationVariables,
          skipTemplateCategories: pageIndex > 0,
        },
        query: GET_TEMPLATES_DEFAULT,
      });

      hasNextPage = false;

      TEMPLATE_CONNECTIONS.forEach(
        ({ responseKey, afterVariable, skipVariable }) => {
          if (paginationVariables[skipVariable]) {
            return;
          }

          const connection = response.data[responseKey];
          items = items.concat(connectionNodes(connection));

          const currentCursor = paginationVariables[afterVariable];
          const nextCursor = connection?.pageInfo?.endCursor || null;
          const canFetchNextPage =
            connection?.pageInfo?.hasNextPage === true &&
            Boolean(nextCursor) &&
            nextCursor !== currentCursor;

          paginationVariables[afterVariable] =
            nextCursor || currentCursor || null;
          paginationVariables[skipVariable] = !canFetchNextPage;
          hasNextPage = hasNextPage || canFetchNextPage;
        }
      );

      if (pageIndex === 0) {
        TEMPLATE_CATEGORY_CONNECTIONS.forEach((responseKey) => {
          categories = categories.concat(
            connectionNodes(response.data[responseKey])
          );
        });
      }

      pageIndex += 1;
    }

    items = items.map((item) => {
      const newItem = _.cloneDeep(item);

      if (newItem.isGlobal) {
        const sortedTemplatePages = _.sortBy(
          item.editor_templates_default_pages.edges,
          ['node.order']
        );
        newItem.editor_templates_default_pages.edges = sortedTemplatePages;
      } else {
        const sortedTemplatePages = _.sortBy(
          item.editor_templates_pages.edges,
          ['node.order']
        );
        newItem.editor_templates_pages.edges = sortedTemplatePages;
      }
      return newItem;
    });

    return {
      categories: {
        allowNew: isGlobalEditor || allowNewCategory || false,
        items: categories,
      },
      templates: {
        allowNew: isGlobalEditor || allowNew || false,
        items,
      },
    };
  } catch (error) {
    onErrorShowMessage();
    console.error(error);
  }
};
